import React, { useCallback, useMemo, useState } from "react";
import {
  Box,
  IconButton,
  Paper,
  Snackbar,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  Facebook,
  Instagram,
  LinkedIn,
  WhatsApp,
  X as XIcon,
} from "@mui/icons-material";

interface ShareButtonsProps {
  url: string;
  title: string;
  description?: string;
}

const ShareButtons: React.FC<ShareButtonsProps> = ({
  url,
  title,
  description,
}) => {
  const [copiedOpen, setCopiedOpen] = useState(false);

  const shareText = useMemo(() => {
    const text = description ? `${title} — ${description}` : title;
    return text.trim();
  }, [description, title]);

  const shareLinks = useMemo(() => {
    const encodedUrl = encodeURIComponent(url);
    const encodedText = encodeURIComponent(shareText);

    return {
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}&quote=${encodedText}`,
      x: `https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodedText}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`,
      whatsapp: `https://api.whatsapp.com/send?text=${encodedText}%20${encodedUrl}`,
    };
  }, [shareText, url]);

  const openPopup = useCallback((shareUrl: string) => {
    window.open(shareUrl, "_blank", "noopener,noreferrer,width=600,height=600");
  }, []);

  const handleInstagramShare = useCallback(async () => {
    // Instagram doesn't have a web share API, so we try:
    // 1. Open Instagram app directly (mobile deep link)
    // 2. Copy link and notify user to paste in Instagram

    // Try Instagram app deep link (works on mobile with Instagram installed)
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

    if (isMobile) {
      // Try to open Instagram app
      window.location.href = "instagram://camera";

      // After a short delay, copy link and show message
      setTimeout(async () => {
        try {
          await navigator.clipboard.writeText(url);
          setCopiedOpen(true);
        } catch {
          // Ignore if clipboard is blocked
        }
      }, 500);
    } else {
      // Desktop: Just copy the link
      try {
        await navigator.clipboard.writeText(url);
        setCopiedOpen(true);
      } catch {
        // Ignore if clipboard is blocked
      }
    }
  }, [url]);

  return (
    <Paper variant="outlined" sx={{ p: 2, my: 3 }}>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={1.5}
        alignItems={{ xs: "flex-start", sm: "center" }}
        justifyContent="space-between"
      >
        <Box>
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            Share this article
          </Typography>
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            Share to your favorite platform
          </Typography>
        </Box>

        <Stack direction="row" spacing={0.5} alignItems="center">
          <Tooltip title="Facebook" arrow>
            <IconButton
              aria-label="Share on Facebook"
              onClick={() => openPopup(shareLinks.facebook)}
              sx={{
                color: "text.secondary",
                "&:hover": { color: "primary.main" },
              }}
            >
              <Facebook />
            </IconButton>
          </Tooltip>

          <Tooltip title="Instagram" arrow>
            <IconButton
              aria-label="Share to Instagram"
              onClick={handleInstagramShare}
              sx={{
                color: "text.secondary",
                "&:hover": { color: "primary.main" },
              }}
            >
              <Instagram />
            </IconButton>
          </Tooltip>

          <Tooltip title="X" arrow>
            <IconButton
              aria-label="Share on X"
              onClick={() => openPopup(shareLinks.x)}
              sx={{
                color: "text.secondary",
                "&:hover": { color: "primary.main" },
              }}
            >
              <XIcon />
            </IconButton>
          </Tooltip>

          <Tooltip title="LinkedIn" arrow>
            <IconButton
              aria-label="Share on LinkedIn"
              onClick={() => openPopup(shareLinks.linkedin)}
              sx={{
                color: "text.secondary",
                "&:hover": { color: "primary.main" },
              }}
            >
              <LinkedIn />
            </IconButton>
          </Tooltip>

          <Tooltip title="WhatsApp" arrow>
            <IconButton
              aria-label="Share on WhatsApp"
              onClick={() => openPopup(shareLinks.whatsapp)}
              sx={{
                color: "text.secondary",
                "&:hover": { color: "primary.main" },
              }}
            >
              <WhatsApp />
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>

      <Snackbar
        open={copiedOpen}
        autoHideDuration={3000}
        onClose={() => setCopiedOpen(false)}
        message="Link copied! Paste it in Instagram to share."
      />
    </Paper>
  );
};

export default ShareButtons;
